import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'motion/react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import { safeFirebaseQuery } from '../utils/firebaseHelper';
import { mockDishes } from '../services/mockData';
import DishCard from '../components/DishCard';
import BestOfArea from '../components/BestOfArea';

function Home({ user, searchQuery }) {

  const [dishOfTheDay, setDishOfTheDay] = useState([]);
  const [dishOfTheWeek, setDishOfTheWeek] = useState([]);
  const [dishOfTheMonth, setDishOfTheMonth] = useState([]);
  const [dishOfTheYear, setDishOfTheYear] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState('day');

  const fetchDishHighlights = useCallback(async () => {
    try {
      setLoading(true);
      const dishesData = await safeFirebaseQuery(
        async () => {
          const dishesSnapshot = await getDocs(collection(db, 'dishes'));
          return dishesSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
        },
        async () => mockDishes,
        'Error fetching dish highlights'
      );

      const now = new Date();
      const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const oneMonthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      const oneYearAgo = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);

      const filterByDate = (dishes, dateThreshold) => {
        return dishes
          .filter(dish => {
            const dishDate = dish.createdAt instanceof Date
              ? dish.createdAt
              : dish.createdAt?.toDate ? dish.createdAt.toDate() : new Date(dish.createdAt);
            return dishDate >= dateThreshold && (dish.voteCount || 0) > 0;
          })
          .sort((a, b) => (b.voteCount || 0) - (a.voteCount || 0))
          .slice(0, 10);
      };

      setDishOfTheDay(filterByDate(dishesData, oneDayAgo));
      setDishOfTheWeek(filterByDate(dishesData, oneWeekAgo));
      setDishOfTheMonth(filterByDate(dishesData, oneMonthAgo));
      setDishOfTheYear(filterByDate(dishesData, oneYearAgo));
    } catch (error) {
      console.error('Error fetching dish highlights:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDishHighlights();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);



  const getCurrentDishes = () => {
    switch (selectedPeriod) {
      case 'day': return dishOfTheDay;
      case 'week': return dishOfTheWeek;
      case 'month': return dishOfTheMonth;
      case 'year': return dishOfTheYear;
      default: return dishOfTheDay;
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-8 py-16 text-center">
        <div className="w-1 h-20 bg-border mx-auto mb-8 animate-pulse"></div>
        <p className="text-muted-foreground">Loading dishes...</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-8">
      {/* Hero Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="text-center mb-20 pt-8"
      >
        <div className="w-1 h-20 bg-border mx-auto mb-8"></div>
        <h1 className="serif text-7xl mb-6">Discover Great Food</h1>
        <p className="text-muted-foreground max-w-2xl mx-auto leading-relaxed">
          Rate and discover the best dishes in your area
        </p>
      </motion.div>

      {/* Best of Your Area */}
      <BestOfArea user={user} />

      {/* Dish Highlights */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="mb-20"
      >
        <div className="flex items-center justify-between mb-12">
          <div>
            <h2 className="serif text-4xl mb-3">Top Dishes</h2>
            <p className="text-sm text-muted-foreground">Most popular dishes by period</p>
          </div>
          <div className="flex items-center gap-4">
            {[
              { id: 'day', label: 'Day' },
              { id: 'week', label: 'Week' },
              { id: 'month', label: 'Month' },
              { id: 'year', label: 'Year' },
            ].map((period) => (
              <button
                key={period.id}
                onClick={() => setSelectedPeriod(period.id)}
                className={`text-sm uppercase tracking-widest transition-colors pb-1 ${selectedPeriod === period.id
                  ? 'text-foreground border-b-2 border-foreground'
                  : 'text-muted-foreground hover:text-foreground'
                  }`}
              >
                {period.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-4 gap-8">
          {getCurrentDishes().slice(0, 8).map((dish, index) => (
            <motion.div
              key={dish.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <DishCard dish={dish} user={user} />
            </motion.div>
          ))}
        </div>
      </motion.section>
    </div>
  );
}

export default Home;
